/**
 * Main plugin builder editor page.
 * Two-pane layout: Conversation/Guided panels (left) | Preview (right)
 * Supports guided flow (6 phases), verbose events, undo/redo,
 * device sizing, screenshots, and auto-approve during execution.
 * On mobile, panes scroll horizontally.
 */

import { useState, useCallback, useEffect, useMemo, useRef, type MutableRefObject } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/hooks/useAuth';
import { usePluginBuilderSession } from '../hooks/usePluginBuilderSession';
import { usePublishStatus } from '../hooks/usePublishStatus';
import { useEditorKeyboardShortcuts } from '../hooks/useEditorKeyboardShortcuts';
import { useMobileHorizontalScroll } from '../hooks/useMobileHorizontalScroll';
import { PluginConversationPane } from '../components/PluginConversationPane';
import { PluginPreviewPane } from '../components/PluginPreviewPane';
import { PluginPromptInput } from '../components/PluginPromptInput';
import { PluginBuilderHeader } from '../components/PluginBuilderHeader';
import { SdkUpdateBanner } from '../components/SdkUpdateBanner';
import { PluginGuidedWelcome } from '../components/PluginGuidedWelcome';
import { PluginConfigPanel } from '../components/PluginConfigPanel';
import { PluginReportProblemDialog } from '../components/PluginReportProblemDialog';
import { AiConfigEditor } from '../components/AiConfigEditor';
import { GuidedPhaseBar } from '../components/guided/GuidedPhaseBar';
import { GuidedInterventionBar } from '../components/guided/GuidedInterventionBar';
import { ClarificationControls } from '../components/guided/ClarificationControls';
import { PlanReview } from '../components/guided/PlanReview';
import { AssumptionReview } from '../components/guided/AssumptionReview';
import { StepExecutionPanel } from '../components/guided/StepExecutionPanel';
import { CompletionBanner } from '../components/guided/CompletionBanner';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Square,
  ArrowRight,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import api from '@/lib/api';
import { authApi } from '@/lib/auth-api';
import { PluginBridgeHost } from '../../../components/PluginBridge';
import { VerboseTimeline } from '../../../components/VerboseTimeline';
import { DARK_THEME, LIGHT_THEME, getThemeById, getFontPairingById } from '@fias/arche-sdk';
import type { GuidedPhase, VerboseEvent, LogEntry, AiConfig } from '../types';
import { useModels } from '@/lib/hooks/useModels';

const API_BASE = import.meta.env.VITE_API_URL || '/v1';

/** djb2 hash helper */
function djb2(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
  }
  return hash;
}

/**
 * Hash files + publish-relevant settings so the publish button detects both
 * code changes and config panel changes (name, visibility, pricing).
 */
function hashPublishState(
  files: Record<string, unknown> | null,
  settings: Record<string, unknown> | null,
  description?: string,
  expandedDescription?: string,
): string {
  let hash = 5381;
  if (files) {
    const keys = Object.keys(files).sort();
    for (const key of keys) {
      const file = files[key] as { content?: string } | string;
      const content = typeof file === 'string' ? file : (file?.content ?? '');
      hash = djb2(key + ':' + content + String(hash));
    }
  }
  if (settings) {
    // Include publish-relevant settings: name, visibility, pricing, aiConfigs
    const { name, visibility, pricing, aiConfigs } = settings as Record<string, unknown>;
    const settingsStr = JSON.stringify({ name, visibility, pricing, aiConfigs });
    hash = djb2(settingsStr + String(hash));
  }
  if (description !== undefined) {
    hash = djb2('desc:' + description + String(hash));
  }
  if (expandedDescription !== undefined) {
    hash = djb2('expdesc:' + expandedDescription + String(hash));
  }
  return hash.toString(36);
}

export function PluginBuilderPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAdmin = useMemo(() => user?.roles?.includes('admin') ?? false, [user?.roles]);
  const { theme: appTheme } = useTheme();

  // ─── Page-level state ───────────────────────────────────────────────────────

  const [inputValue, setInputValue] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSubmissionId, setPublishSubmissionId] = useState<string | null>(null);
  const [lastPublishedHash, setLastPublishedHash] = useState<string | null>(null);
  const [localSettings, setLocalSettings] = useState<Record<string, unknown> | null>(null);
  const handledSubmissionRef = useRef<string | null>(null);
  const [suggestedNextSteps, setSuggestedNextSteps] = useState<string[]>([]);
  const [verbose, setVerbose] = useState(false);
  const [deepThinking, setDeepThinking] = useState(false);
  const [autoApprove, setAutoApprove] = useState(false);
  const autoApproveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevIsSendingForAutoApproveRef = useRef(false);
  const userStoppedRef = useRef(false);
  const criteriaAutoContinuedRef = useRef(false);
  const [guidedViewMode, setGuidedViewMode] = useState<'panel' | 'conversation'>('panel');
  const [guidedIdea, setGuidedIdea] = useState('');
  const [guidedName, setGuidedName] = useState('');
  const [selectedThemeId, setSelectedThemeId] = useState('fias-dark');
  const [selectedFontPairingId, setSelectedFontPairingId] = useState('system');
  const [customTheme, setCustomTheme] = useState<import('@fias/arche-sdk').FiasTheme | undefined>();
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState<string | null>(null);
  const { models: modelDefinitions } = useModels();
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>(
    appTheme === 'light' ? 'light' : 'dark',
  );
  const [logEntries, setLogEntries] = useState<LogEntry[]>([]);
  const unflushedLogsRef = useRef<LogEntry[]>([]);
  // Latch: once the guided UI shows the refine state, it must not flip back to
  // "build" just because the user sends another message (which toggles
  // isSending true and would otherwise drop showRefineState). Reset only on
  // phase transitions out of execution/complete (e.g. rewind to planning).
  const [hasReachedRefine, setHasReachedRefine] = useState(false);

  // Cap retained log entries so long-running sessions (especially ones with
  // chatty preview code) don't grow the array unbounded and blow up the
  // problem-report payload. Older entries are dropped FIFO.
  const MAX_LOG_ENTRIES = 2000;
  const appendLogEntry = useCallback((entry: LogEntry) => {
    setLogEntries((prev) =>
      prev.length >= MAX_LOG_ENTRIES
        ? [...prev.slice(-(MAX_LOG_ENTRIES - 1)), entry]
        : [...prev, entry],
    );
    unflushedLogsRef.current.push(entry);
  }, []);

  // Publish pipeline status polling
  const publishStatus = usePublishStatus(publishSubmissionId);

  // Ref-based theme resolver for the bridge's getTheme() closure.
  // Updated after session hook provides currentSettings.
  const selectedThemeRef = useRef<{
    themeId?: string;
    fontPairingId?: string;
    customTheme?: import('@fias/arche-sdk').FiasTheme;
  }>({});
  const previewThemeRef = useRef(previewTheme);
  previewThemeRef.current = previewTheme;

  // Sync preview theme when the app theme changes
  useEffect(() => {
    setPreviewTheme(appTheme === 'light' ? 'light' : 'dark');
  }, [appTheme]);

  // ─── Verbose event state (RAF-batched) ──────────────────────────────────────

  const [verboseHistory, setVerboseHistory] = useState<Record<string, VerboseEvent[]>>({});
  const [pendingVerboseEvents, setPendingVerboseEvents] = useState<VerboseEvent[]>([]);
  const pendingVerboseRef = useRef<VerboseEvent[]>([]);
  const prevIsSendingRef = useRef(false);
  const flushRafRef = useRef<number | null>(null) as MutableRefObject<number | null>;

  const handleVerboseEvent = useCallback((event: VerboseEvent) => {
    if (event.type === 'model_info') {
      pendingVerboseRef.current = [event];
    } else {
      pendingVerboseRef.current = [...pendingVerboseRef.current, event];
    }
    if (flushRafRef.current === null) {
      flushRafRef.current = requestAnimationFrame(() => {
        setPendingVerboseEvents([...pendingVerboseRef.current]);
        flushRafRef.current = null;
      });
    }
  }, []);

  // ─── Session hook ───────────────────────────────────────────────────────────

  const {
    session,
    isLoading,
    error,
    files,
    messages,
    streamingText,
    isSending,
    guidedFlow,
    buildPlan,
    builderMode,
    isGuidedLoading,
    canUndo,
    canRedo,
    undoCount,
    redoCount,
    undo,
    redo,
    loadSession,
    sendMessage,
    startGuidedFlow,
    setBuilderMode,
    resetContext,
    stopGeneration,
    uploadPreviewScreenshot,
    advanceStep,
    submitAnswers,
    approvePlan,
    requestPlanChanges,
    approveAssumptions,
    suggestAssumptionChanges,
    applyPendingGuidedResult,
    hasPendingGuidedResult,
    cancelGuidedRequest,
    rewindToPhase,
    buildPreview,
    previewHtml,
    isBuildingPreview,
    buildError,
    isResetting,
    sdkUpdate,
    isUpgradingSdk,
    upgradeSdk,
    setSessionName,
    updateSessionSettings,
    setBuildPlan,
  } = usePluginBuilderSession({
    verbose,
    useOpus: deepThinking,
    onVerboseEvent: handleVerboseEvent,
    onError: (err) => {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
        duration: 8000,
      });
    },
  });

  // ─── Derived state ──────────────────────────────────────────────────────────

  // Detect whether files or settings have changed since last publish
  const currentSettings = (session?.currentSettings ?? localSettings) as Record<
    string,
    unknown
  > | null;
  const currentDescription = session?.currentDescription ?? '';
  const currentExpandedDescription = session?.currentExpandedDescription ?? '';
  const currentPublishHash = useMemo(
    () =>
      hashPublishState(
        files,
        localSettings ?? currentSettings,
        currentDescription,
        currentExpandedDescription,
      ),
    [files, localSettings, currentSettings, currentDescription, currentExpandedDescription],
  );
  const hasUnpublishedChanges =
    lastPublishedHash === null || currentPublishHash !== lastPublishedHash;

  // Keep the theme ref in sync so the bridge always returns the selected catalog theme
  const effectiveSettings = localSettings ?? currentSettings;
  selectedThemeRef.current = {
    themeId: effectiveSettings?.themeId as string | undefined,
    fontPairingId: effectiveSettings?.fontPairingId as string | undefined,
    customTheme: effectiveSettings?.customTheme as import('@fias/arche-sdk').FiasTheme | undefined,
  };

  const guidedPhase: GuidedPhase = guidedFlow?.phase ?? 'ideation';
  const isGuidedMode = builderMode === 'guided';
  const aiConfigs = (currentSettings?.aiConfigs as AiConfig[] | undefined) ?? [];

  const isSimpleBuild =
    buildPlan != null && (buildPlan.graph?.nodes?.length ?? buildPlan.steps.length) <= 1;

  const allStepsVerified =
    buildPlan?.approved &&
    (buildPlan.graph
      ? buildPlan.graph.nodes.every((n) => n.status === 'verified')
      : buildPlan.steps.length > 0 && buildPlan.steps.every((s) => s.status === 'verified'));
  const hasPendingCriteria =
    allStepsVerified &&
    (buildPlan?.completionCriteria ?? []).length > 0 &&
    (buildPlan?.completionCriteria ?? []).some((c) => !c.verified);
  const showRefineState =
    guidedPhase === 'complete' ||
    (guidedPhase === 'execution' && (hasReachedRefine || (allStepsVerified && !isSending)));

  // ─── Effects ────────────────────────────────────────────────────────────────

  // Load session on mount
  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    }
  }, [sessionId, loadSession]);

  // Resume publish polling if a submission was in progress when session loads.
  // If the session has actually been published (arche status is not draft) and no active submission,
  // snapshot current files so publish button is grayed until changes are made.
  useEffect(() => {
    if (session?.currentSettings) {
      const settings = session.currentSettings as Record<string, unknown>;
      const existingSubmission = settings.lastPublishSubmissionId as string | null;
      if (
        existingSubmission &&
        !publishSubmissionId &&
        existingSubmission !== handledSubmissionRef.current
      ) {
        if (session.isPublished) {
          // Already published — mark as handled so we don't re-poll and show a toast on page load
          handledSubmissionRef.current = existingSubmission;
        } else {
          // Still in progress — resume polling
          setPublishSubmissionId(existingSubmission);
        }
      }
      // If session was previously published and no active submission, mark as "clean"
      if (session.isPublished && lastPublishedHash === null) {
        setLastPublishedHash(
          hashPublishState(
            files,
            settings as Record<string, unknown>,
            session.currentDescription ?? '',
            session.currentExpandedDescription ?? '',
          ),
        );
        // Initialize local settings from session so config panel changes are tracked
        if (!localSettings) {
          setLocalSettings(settings as Record<string, unknown>);
        }
      }
    }
  }, [
    session?.currentSettings,
    session?.isPublished,
    publishSubmissionId,
    files,
    lastPublishedHash,
    localSettings,
  ]);

  // Handle publish terminal states: show toasts and re-enable publish button.
  // Uses currentPublishHash (already computed from files + settings) to snapshot,
  // avoiding stale closure issues with files/settings directly.
  useEffect(() => {
    if (!publishStatus.submissionId) return;

    if (publishStatus.status === 'published') {
      toast({
        title: 'Arche Published',
        description: 'Your arche is now live on the marketplace.',
      });
      // Snapshot current hash so publish button grays until new changes
      setLastPublishedHash(currentPublishHash);
      // Mark as handled so resume-polling doesn't re-trigger
      handledSubmissionRef.current = publishStatus.submissionId;
      setPublishSubmissionId(null);
    } else if (publishStatus.status === 'rejected') {
      toast({
        title: 'Review Rejected',
        description: publishStatus.errorMessage || 'Your arche did not pass the review.',
        variant: 'destructive',
        duration: 8000,
      });
      handledSubmissionRef.current = publishStatus.submissionId;
      setPublishSubmissionId(null);
    } else if (publishStatus.status === 'failed') {
      toast({
        title: 'Build Failed',
        description: publishStatus.errorMessage || 'The arche build failed.',
        variant: 'destructive',
        duration: 8000,
      });
      handledSubmissionRef.current = publishStatus.submissionId;
      setPublishSubmissionId(null);
    }
  }, [
    publishStatus.status,
    publishStatus.submissionId,
    publishStatus.errorMessage,
    toast,
    currentPublishHash,
  ]);

  // Seed guided idea/name from server data when session loads
  useEffect(() => {
    if (guidedFlow?.ideaSummary) {
      setGuidedIdea((prev) => prev || guidedFlow.ideaSummary);
    }
  }, [guidedFlow?.ideaSummary]);

  useEffect(() => {
    if (session?.currentName) {
      setGuidedName((prev) => prev || session.currentName || '');
    }
  }, [session?.currentName]);

  // Resolve builder mode on load. Prefer the persisted choice from
  // NewArcheDialog (current_settings.guidedBuilderState.mode), falling back
  // to the legacy auto-detect for sessions created before mode was locked
  // at creation time.
  useEffect(() => {
    if (!session) return;
    const persistedMode = (
      session.currentSettings as { guidedBuilderState?: { mode?: 'chat' | 'guided' } } | null
    )?.guidedBuilderState?.mode;
    if (persistedMode === 'chat' || persistedMode === 'guided') {
      setBuilderMode(persistedMode);
      return;
    }
    if (guidedFlow) {
      setBuilderMode('guided');
    } else if (session.conversationHistory.length > 0) {
      setBuilderMode('chat');
    }
  }, [session?.sessionId, guidedFlow?.phase]);

  // Auto-switch to panel view when entering a phase that has a guided panel
  const prevGuidedPhaseRef = useRef(guidedPhase);
  useEffect(() => {
    if (guidedPhase !== prevGuidedPhaseRef.current) {
      const prev = prevGuidedPhaseRef.current;
      prevGuidedPhaseRef.current = guidedPhase;
      if (
        guidedPhase === 'clarification' ||
        guidedPhase === 'planning' ||
        guidedPhase === 'assumptions'
      ) {
        setGuidedViewMode('panel');
      }
      // Auto-switch to conversation view when entering complete (refine) phase
      if (guidedPhase === 'complete') {
        setGuidedViewMode('conversation');
      }
      // Auto-start building when transitioning into execution after assumption approval
      if (prev === 'assumptions' && guidedPhase === 'execution' && buildPlan?.approved) {
        setGuidedViewMode('conversation');
        if (!isSending) {
          const executionMessage = isSimpleBuild
            ? 'Build the complete plugin now.'
            : autoApprove
              ? 'Begin building the approved plan. Build everything needed, verifying each step as you go.'
              : 'Begin building the approved plan. Start with shared infrastructure (App.tsx, context, types), then build the first step.';
          sendMessage(executionMessage, 'execution', autoApprove);
        }
      }
    }
  }, [guidedPhase, buildPlan?.approved, isSending, sendMessage, isSimpleBuild, autoApprove]);

  // Latch hasReachedRefine once the refine state is visible, and reset it when
  // the guided flow leaves execution/complete (e.g. user rewinds to planning).
  // Without the latch, sending a follow-up refine message toggles isSending
  // true and flips the phase bar from "Refine" back to "Build".
  useEffect(() => {
    if (guidedPhase === 'execution' && allStepsVerified && !isSending) {
      setHasReachedRefine(true);
    } else if (guidedPhase !== 'execution' && guidedPhase !== 'complete') {
      setHasReachedRefine(false);
    }
  }, [guidedPhase, allStepsVerified, isSending]);

  // Auto-switch back to panel when AI finishes responding during planning phase.
  // When a pending guided result arrives, auto-apply it and switch to the panel.
  // Also auto-switch to conversation when entering refine state during execution.
  useEffect(() => {
    if (prevIsSendingRef.current && !isSending) {
      if (isGuidedMode && guidedPhase === 'planning' && guidedViewMode === 'conversation') {
        if (hasPendingGuidedResult) {
          applyPendingGuidedResult();
        }
        setGuidedViewMode('panel');
      }
      // Switch to conversation view when build completes (refine state)
      if (isGuidedMode && guidedPhase === 'execution' && allStepsVerified) {
        setGuidedViewMode('conversation');
      }
    }
  }, [
    isSending,
    isGuidedMode,
    guidedPhase,
    buildPlan,
    guidedViewMode,
    hasPendingGuidedResult,
    applyPendingGuidedResult,
    allStepsVerified,
  ]);

  // Keep a ref to messages for use in the verbose commit effect
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // Commit pending verbose events when streaming transitions from active to idle
  const prevIsGuidedLoadingRef = useRef(false);
  useEffect(() => {
    const wasSending = prevIsSendingRef.current && !isSending;
    const wasGuidedLoading = prevIsGuidedLoadingRef.current && !isGuidedLoading;
    if (wasSending || wasGuidedLoading) {
      const events = pendingVerboseRef.current;
      if (events.length > 0) {
        const lastMsg = messagesRef.current[messagesRef.current.length - 1];
        if (lastMsg && lastMsg.role === 'assistant') {
          setVerboseHistory((prev) => ({
            ...prev,
            [lastMsg.timestamp || String(Date.now())]: events,
          }));
        }
      }
      pendingVerboseRef.current = [];
      setPendingVerboseEvents([]);
    }
    prevIsSendingRef.current = isSending;
    prevIsGuidedLoadingRef.current = isGuidedLoading;
  }, [isSending, isGuidedLoading]);

  // Stable ref to sendMessage so the auto-approve timer isn't nuked when
  // sendMessage's identity churns mid-window. usePluginBuilderSession recreates
  // sendMessage whenever isSending changes, so reading via ref keeps the
  // 2-second timer alive even if the user interacts during the window.
  const sendMessageRef = useRef(sendMessage);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  // Auto-approve effect: when auto-approve is ON and AI finishes a step
  useEffect(() => {
    const wasSending = prevIsSendingForAutoApproveRef.current;
    prevIsSendingForAutoApproveRef.current = isSending;

    if (!autoApprove || !isGuidedMode || guidedPhase !== 'execution') return;

    // Don't auto-continue when all steps are already verified
    if (allStepsVerified) return;

    if (!isSending && wasSending) {
      // If the user pressed stop, don't auto-continue
      if (userStoppedRef.current) {
        userStoppedRef.current = false;
        return;
      }
      autoApproveTimerRef.current = setTimeout(() => {
        sendMessageRef.current(
          'Continue. If the current step is fully built, verify and advance to the next step.',
          'execution',
          true,
        );
      }, 2000);
    }

    return () => {
      if (autoApproveTimerRef.current) {
        clearTimeout(autoApproveTimerRef.current);
        autoApproveTimerRef.current = null;
      }
    };
  }, [isSending, autoApprove, isGuidedMode, guidedPhase, allStepsVerified]);

  // Auto-continue once when all steps verified but criteria still pending.
  // Only fires on an actual isSending transition (true→false), NOT on initial mount/refresh.
  // Must NOT fire during refine state — the user controls the flow at that point.
  const prevIsSendingForCriteriaRef = useRef(isSending);
  useEffect(() => {
    const wasSending = prevIsSendingForCriteriaRef.current;
    prevIsSendingForCriteriaRef.current = isSending;

    if (
      wasSending &&
      !isSending &&
      guidedPhase === 'execution' &&
      !showRefineState &&
      allStepsVerified &&
      hasPendingCriteria &&
      !criteriaAutoContinuedRef.current
    ) {
      criteriaAutoContinuedRef.current = true;
      sendMessage('Continue. Address the remaining completion criteria.', 'complete', autoApprove);
    }
  }, [
    isSending,
    guidedPhase,
    showRefineState,
    allStepsVerified,
    hasPendingCriteria,
    sendMessage,
    autoApprove,
  ]);

  // Listen for LOG_ENTRY and SCREENSHOT_RESULT from preview iframe
  // (These are HTML template features, not bridge protocol messages)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our own preview iframe
      if (event.source !== previewIframeRef.current?.contentWindow) return;
      if (!event.data || typeof event.data !== 'object') return;
      if (event.data.type === 'LOG_ENTRY' && event.data.entry) {
        const entry = event.data.entry as LogEntry;
        appendLogEntry(entry);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [appendLogEntry]);

  // Initialize PluginBridgeHost when iframe loads with preview content
  const handlePreviewIframeLoad = useCallback(() => {
    if (!previewIframeRef.current || !sessionId) return;

    // Destroy previous bridge instance
    bridgeRef.current?.destroy();

    bridgeRef.current = new PluginBridgeHost({
      archId: `preview_${sessionId}`,
      permissions: [
        'user:profile:read',
        'entities:invoke',
        'storage:sandbox',
        'data:store',
        'theme:read',
      ],
      iframe: previewIframeRef.current,
      previewMode: true,
      previewStorage: previewStorageRef.current,
      onEntityInvoke: async (payload) => {
        const result = await api.invokePluginAI(sessionId, payload.input, payload.entityId);
        return { output: result.output };
      },
      onImageGenerate: async (payload) => {
        return api.generatePluginImage(sessionId, payload);
      },
      onStorage: async (action, params) => {
        return api.previewStorage(
          sessionId,
          action as 'read' | 'write' | 'list' | 'delete',
          params as { path?: string; content?: string; prefix?: string },
        );
      },
      onLog: (level, message) => {
        const entry: LogEntry = {
          id: `bridge-${Date.now()}`,
          timestamp: Date.now(),
          level,
          category: 'general',
          message,
          source: 'bridge',
        };
        appendLogEntry(entry);
      },
      getTheme: () => {
        const { themeId, fontPairingId, customTheme } = selectedThemeRef.current;
        const targetMode = previewThemeRef.current;
        let theme =
          customTheme ??
          (themeId ? getThemeById(themeId)?.theme : undefined) ??
          (targetMode === 'dark' ? DARK_THEME : LIGHT_THEME);
        // When the preview toggle mode differs from the resolved theme's mode,
        // swap to the matching Fias default so the preview actually changes visually.
        if (theme.mode !== targetMode) {
          theme = targetMode === 'dark' ? DARK_THEME : LIGHT_THEME;
        }
        // Merge selected font pairing into the theme so useFiasTheme().fonts reflects it
        if (fontPairingId) {
          const fp = getFontPairingById(fontPairingId);
          if (fp) {
            theme = { ...theme, fonts: { body: fp.body, heading: fp.heading, mono: fp.mono } };
          }
        }
        return theme;
      },
      getUser: async () => ({
        userId: user?.userId ?? '',
        displayName: user?.username ?? 'Preview User',
        avatar: null,
      }),
      bridgeApiUrl: API_BASE,
      getAuthToken: async () => {
        const token = await authApi.getValidIdToken();
        return token ?? '';
      },
    });

    // Send init with retry — if the iframe's module scripts haven't finished
    // evaluating, the FiasBridge listener may not be registered yet.
    bridgeRef.current.init();
    const retryTimer = setTimeout(() => {
      bridgeRef.current?.init();
    }, 3000);

    return () => clearTimeout(retryTimer);
  }, [sessionId, user, appendLogEntry]);

  // Clean up bridge on unmount
  useEffect(() => {
    return () => {
      bridgeRef.current?.destroy();
    };
  }, []);

  // Clear preview storage when switching sessions (not on every rebuild)
  useEffect(() => {
    previewStorageRef.current = new Map<string, string>();
  }, [sessionId]);

  // Debounce-POST logs to dev endpoint for external visibility (Claude Code, Fias AI tools)
  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const timer = setTimeout(() => {
      fetch('/v1/dev/plugin-builder-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logs: logEntries, files }),
      }).catch(() => {
        // Dev convenience — not critical path
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [logEntries, files]);

  // Cleanup RAF and timers on unmount
  useEffect(() => {
    return () => {
      if (flushRafRef.current !== null) {
        cancelAnimationFrame(flushRafRef.current);
      }
      if (autoApproveTimerRef.current) {
        clearTimeout(autoApproveTimerRef.current);
      }
    };
  }, []);

  // Iframe ref for posting messages back to the preview iframe
  const previewIframeRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<PluginBridgeHost | null>(null);
  // Persistent storage map shared across bridge rebuilds (survives preview reloads)
  const previewStorageRef = useRef(new Map<string, string>());

  // Track which pane was last clicked for scoped Ctrl+A selection
  const conversationPaneRef = useRef<HTMLDivElement>(null);
  const previewPaneRef = useRef<HTMLDivElement>(null);
  const activePaneRef = useRef<HTMLDivElement | null>(null);

  const handlePaneFocus = useCallback((paneRef: React.RefObject<HTMLDivElement | null>) => {
    activePaneRef.current = paneRef.current;
  }, []);

  // Keyboard shortcuts for undo/redo and scoped Ctrl+A
  useEditorKeyboardShortcuts({ undo, redo, activePaneRef });

  // ─── Mobile horizontal scroll ──────────────────────────────────────────────

  const {
    panelsContainerRef,
    scrollTrackRef,
    scrollState,
    handleScrollbarDrag,
    scrollToPanel,
    isDraggingRef,
  } = useMobileHorizontalScroll(isLoading);

  // ─── Callbacks ──────────────────────────────────────────────────────────────

  // Flush buffered preview logs to the backend so get_preview_logs has them
  const flushPreviewLogs = useCallback(() => {
    const entries = unflushedLogsRef.current;
    if (entries.length === 0 || !sessionId) return;
    unflushedLogsRef.current = [];
    api.uploadPluginPreviewLogs(sessionId, entries).catch(() => {
      // Non-critical — logs are best-effort
    });
  }, [sessionId]);

  const handlePromptSubmit = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed || isSending) return;
    setInputValue('');
    flushPreviewLogs();
    // During guided flow, always pass the current phase so the backend uses
    // phase-appropriate tools and system prompt (prevents fallthrough to ungoverned chat).
    const phase =
      isGuidedMode && guidedFlow ? (showRefineState ? 'complete' : guidedPhase) : undefined;
    sendMessage(trimmed, phase, phase === 'execution' ? autoApprove : undefined);
  }, [
    inputValue,
    isSending,
    sendMessage,
    flushPreviewLogs,
    isGuidedMode,
    guidedPhase,
    guidedFlow,
    showRefineState,
    autoApprove,
  ]);

  const handlePublish = useCallback(async (): Promise<{ submissionId: string } | null> => {
    if (!sessionId) return null;
    setIsPublishing(true);
    try {
      const result = await api.publishPluginBuilder(sessionId);
      setPublishSubmissionId(result.submissionId);
      return { submissionId: result.submissionId };
    } catch (err) {
      const error = err as Error & { submissionId?: string };
      // If there's already a submission in progress, resume polling for it
      if (error.submissionId) {
        setPublishSubmissionId(error.submissionId);
      }
      toast({
        title: 'Publish Failed',
        description: error.message,
        variant: 'destructive',
        duration: 8000,
      });
      return null;
    } finally {
      setIsPublishing(false);
    }
  }, [sessionId, toast]);

  const handleUnpublish = useCallback(async () => {
    if (!sessionId) return;
    try {
      await api.unpublishPluginSession(sessionId);
      // Reset publish state so user can re-publish
      setLastPublishedHash(null);
      setPublishSubmissionId(null);
      // Force reload session to pick up the new arche status
      loadSession(sessionId);
      toast({
        title: 'Arche Unpublished',
        description: 'Your arche has been removed from the marketplace.',
      });
    } catch (err) {
      toast({
        title: 'Unpublish Failed',
        description: (err as Error).message,
        variant: 'destructive',
        duration: 8000,
      });
    }
  }, [sessionId, loadSession, toast]);

  const handleResetContext = useCallback(async () => {
    await resetContext();
    setSuggestedNextSteps([]);
    setVerboseHistory({});
    setPendingVerboseEvents([]);
    pendingVerboseRef.current = [];
    if (flushRafRef.current !== null) {
      cancelAnimationFrame(flushRafRef.current);
      flushRafRef.current = null;
    }
    toast({
      title: 'Context Reset',
      description: 'Conversation history cleared. Your arche files have been preserved.',
    });
  }, [resetContext, toast]);

  // Track the last theme/font applied to the preview so we can rebuild when they change
  const lastThemeRef = useRef<string>('');

  const handleSettingsChange = useCallback(
    async (settings: unknown) => {
      if (!sessionId) return;
      setLocalSettings(settings as Record<string, unknown>);
      updateSessionSettings(settings as Record<string, unknown>);
      await api.updatePluginBuilderSession(sessionId, { currentSettings: settings });

      // Rebuild preview when theme or font changes so the HTML shell gets updated theme CSS,
      // and push the new theme to the plugin via bridge so useFiasTheme() returns it immediately
      const s = settings as Record<string, unknown>;
      const themeKey = `${s.themeId ?? ''}|${s.fontPairingId ?? ''}|${JSON.stringify(s.customTheme ?? null)}`;
      if (lastThemeRef.current && themeKey !== lastThemeRef.current) {
        bridgeRef.current?.sendThemeUpdate();
        buildPreview(files ?? undefined);
      }
      lastThemeRef.current = themeKey;
    },
    [sessionId, updateSessionSettings, buildPreview, files],
  );

  // Called from PluginConfigPanel when the plugin name is saved.
  // Optimistically updates the header and persists to the currentName column.
  const handleConfigNameChange = useCallback(
    (newName: string) => {
      setSessionName(newName);
      if (sessionId) {
        api.updatePluginBuilderSession(sessionId, { currentName: newName });
      }
    },
    [sessionId, setSessionName],
  );

  // Called from PluginConfigPanel when the short description is saved.
  // The backend routes this to arche.simple_description (single source of truth).
  const handleConfigDescriptionChange = useCallback(
    (newDescription: string) => {
      if (sessionId) {
        api.updatePluginBuilderSession(sessionId, { currentDescription: newDescription });
      }
    },
    [sessionId],
  );

  // Called from PluginConfigPanel when the expanded description ("About") is saved.
  const handleConfigExpandedDescriptionChange = useCallback(
    (newExpandedDescription: string) => {
      if (sessionId) {
        api.updatePluginBuilderSession(sessionId, {
          currentExpandedDescription: newExpandedDescription,
        });
      }
    },
    [sessionId],
  );

  // ─── Guided flow callbacks ──────────────────────────────────────────────────

  const handleGuidedStart = useCallback(
    async (idea: string, name?: string) => {
      if (!sessionId) return;
      try {
        const themeSettings: Record<string, unknown> = {
          themeId: selectedThemeId,
          fontPairingId: selectedFontPairingId,
        };
        if (customTheme) themeSettings.customTheme = customTheme;
        updateSessionSettings(themeSettings);

        // Persist name + theme in a single PATCH so nothing races with
        // POST /guided/start — concurrent writes trip optimistic locking.
        if (name) setSessionName(name);
        const patch: Record<string, unknown> = { currentSettings: themeSettings };
        if (name) patch.currentName = name;
        await api.updatePluginBuilderSession(sessionId, patch);

        setGuidedViewMode('conversation');
        await startGuidedFlow(idea);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [
      sessionId,
      setSessionName,
      startGuidedFlow,
      toast,
      selectedThemeId,
      selectedFontPairingId,
      customTheme,
      updateSessionSettings,
    ],
  );

  const handleSubmitAnswers = useCallback(
    async (answers: Record<number, string>, comment?: string) => {
      try {
        setGuidedViewMode('conversation');
        await submitAnswers(answers, comment);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [submitAnswers, toast],
  );

  const handlePlanReviewApprove = useCallback(async () => {
    try {
      setGuidedViewMode('conversation');
      await approvePlan();
    } catch (err) {
      toast({
        title: 'Error',
        description: (err as Error).message,
        variant: 'destructive',
        duration: 8000,
      });
    }
  }, [approvePlan, toast]);

  const handlePlanReviewChanges = useCallback(
    async (feedback: string) => {
      try {
        setGuidedViewMode('conversation');
        await requestPlanChanges(feedback);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [requestPlanChanges, toast],
  );

  const handleSuggestAssumptionChanges = useCallback(
    async (comments: Record<string, string>) => {
      try {
        setGuidedViewMode('conversation');
        await suggestAssumptionChanges(comments);
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [suggestAssumptionChanges, toast],
  );

  const handleApprovePlan = useCallback(async () => {
    try {
      setGuidedViewMode('conversation');
      await approveAssumptions();
    } catch (err) {
      toast({
        title: 'Error',
        description: (err as Error).message,
        variant: 'destructive',
        duration: 8000,
      });
    }
  }, [approveAssumptions, toast]);

  const handleContinueTransition = useCallback(async () => {
    applyPendingGuidedResult();
    // If we're in the assumptions phase, approve and move to build
    if (guidedPhase === 'assumptions') {
      try {
        setGuidedViewMode('conversation');
        await approveAssumptions();
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    }
  }, [applyPendingGuidedResult, guidedPhase, approveAssumptions, toast]);

  const handleResumeGuidedFlow = useCallback(async () => {
    setBuilderMode('guided');

    // Check if the current phase has panel data to show
    const hasData =
      (guidedPhase === 'clarification' && (guidedFlow?.clarifyingQA ?? []).length > 0) ||
      (guidedPhase === 'planning' && buildPlan !== undefined) ||
      (guidedPhase === 'assumptions' &&
        ((guidedFlow?.assumptions ?? []).length > 0 || buildPlan !== undefined));

    if (hasData) {
      setGuidedViewMode('panel');
      return;
    }

    // No panel data for the current phase — re-start the guided flow from
    // the existing idea. This triggers the full pipeline (ideation → clarification → ...).
    const idea = guidedFlow?.ideaSummary;
    if (idea) {
      try {
        setGuidedViewMode('conversation');
        await startGuidedFlow(idea);
      } catch (err) {
        toast({
          title: 'Error resuming guided flow',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    } else {
      // No idea summary — just switch to conversation view
      setGuidedViewMode('conversation');
    }
  }, [
    guidedPhase,
    guidedFlow?.clarifyingQA,
    guidedFlow?.assumptions,
    guidedFlow?.ideaSummary,
    buildPlan,
    setBuilderMode,
    startGuidedFlow,
    toast,
  ]);

  const handlePhaseClick = useCallback(
    async (phase: GuidedPhase) => {
      // No-op if clicking the current phase
      if (phase === guidedFlow?.phase) return;

      try {
        await rewindToPhase(phase);
        // Switch to the appropriate view for the target phase
        if (phase === 'clarification' || phase === 'planning' || phase === 'assumptions') {
          setGuidedViewMode('panel');
        } else {
          setGuidedViewMode('conversation');
        }
      } catch (err) {
        toast({
          title: 'Error',
          description: (err as Error).message,
          variant: 'destructive',
        });
      }
    },
    [rewindToPhase, guidedFlow?.phase, toast],
  );

  const handleToggleNodeUI = useCallback(
    async (nodeId: string, showUI: boolean) => {
      if (!buildPlan?.graph) return;
      const updatedNodes = buildPlan.graph.nodes.map((n) =>
        n.id === nodeId ? { ...n, showUI } : n,
      );
      const updatedPlan = {
        ...buildPlan,
        graph: { ...buildPlan.graph, nodes: updatedNodes },
      };
      setBuildPlan(updatedPlan);
      await updateSessionSettings({ buildPlan: updatedPlan });
    },
    [buildPlan, setBuildPlan, updateSessionSettings],
  );

  const handleExecutionFeedback = useCallback(
    async (message: string) => {
      // Keep userStoppedRef true so the auto-approve effect doesn't fire after
      // the AI responds to the user's feedback. The user must explicitly click
      // "Continue" to resume auto-advance.
      await sendMessage(message, 'execution', autoApprove);
    },
    [sendMessage, autoApprove],
  );

  const handleStopFromPanel = useCallback(() => {
    if (autoApproveTimerRef.current) {
      clearTimeout(autoApproveTimerRef.current);
      autoApproveTimerRef.current = null;
    }
    userStoppedRef.current = true;
    setAutoApprove(false);
    stopGeneration();
    setGuidedViewMode('conversation');
  }, [stopGeneration]);

  // ─── Dynamic placeholder ────────────────────────────────────────────────────

  const guidedPlaceholder = useMemo(() => {
    if (!isGuidedMode) return undefined;
    switch (guidedPhase) {
      case 'ideation':
        return 'Describe what plugin you want to build...';
      case 'clarification':
        return "Answer the AI's questions...";
      case 'planning':
        return 'Review the plan or request changes...';
      case 'execution':
        return autoApprove ? 'Auto-building steps...' : 'Chat about the current step...';
      case 'complete':
        return 'Continue chatting to refine your plugin...';
      default:
        return undefined;
    }
  }, [isGuidedMode, guidedPhase, autoApprove]);

  // ─── Early returns ──────────────────────────────────────────────────────────

  if (!sessionId) {
    navigate('/a/arche_plugin_builder');
    return null;
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-destructive">Failed to load session</p>
        <Button onClick={() => navigate('/a/arche_plugin_builder')}>Back to Sessions</Button>
      </div>
    );
  }

  // ─── Prompt input props ─────────────────────────────────────────────────────

  const hasMessages = messages.length > 0;

  const promptInputProps = {
    value: inputValue,
    onChange: setInputValue,
    onSubmit: handlePromptSubmit,
    onStopGeneration: stopGeneration,
    onResetContext: handleResetContext,
    isProcessing: isSending,
    isResetting,
    hasMessages,
    placeholder: guidedPlaceholder,
  };

  // ─── Guided panel detection ─────────────────────────────────────────────────

  const clarifyingQA = guidedFlow?.clarifyingQA ?? [];
  const hasClarificationPanel =
    isGuidedMode && guidedPhase === 'clarification' && clarifyingQA.length > 0;
  const hasPlanningPanel = isGuidedMode && guidedPhase === 'planning' && buildPlan !== undefined;
  const isPlanGenerating =
    isGuidedMode &&
    guidedPhase === 'planning' &&
    buildPlan === undefined &&
    (isSending || isGuidedLoading);
  const hasAssumptionsPanel =
    isGuidedMode &&
    guidedPhase === 'assumptions' &&
    ((guidedFlow?.assumptions ?? []).length > 0 || buildPlan !== undefined);

  // Detect guided loading states that show a full-panel loading indicator with cancel
  const isGuidedLoadingWithCancel =
    isGuidedMode &&
    isGuidedLoading &&
    !streamingText && // Don't show spinner overlay when text is streaming
    !hasClarificationPanel &&
    !hasPlanningPanel &&
    !hasAssumptionsPanel;

  const guidedLoadingMessage = (() => {
    if (!isGuidedLoadingWithCancel) return { title: '', subtitle: '' };
    switch (guidedPhase) {
      case 'ideation':
        return {
          title: 'Generating questions...',
          subtitle: 'Analyzing your idea and preparing clarifying questions.',
        };
      case 'clarification':
        return {
          title: 'Generating build plan...',
          subtitle: 'Researching patterns and designing the steps for your plugin.',
        };
      case 'planning':
        return {
          title: 'Generating...',
          subtitle: 'Researching patterns and designing the steps for your plugin.',
        };
      case 'assumptions':
        return {
          title: 'Revising assumptions...',
          subtitle: 'Updating assumptions based on your feedback.',
        };
      default:
        return {
          title: 'Processing...',
          subtitle: 'Please wait while the AI works on your request.',
        };
    }
  })();

  const hasGuidedPanel =
    hasClarificationPanel ||
    hasPlanningPanel ||
    isPlanGenerating ||
    hasAssumptionsPanel ||
    isGuidedLoadingWithCancel;
  const showGuidedPanel = hasGuidedPanel && guidedViewMode === 'panel';

  // In step-by-step mode, show the prompt input when AI is idle so user can
  // chat about the current step (review, request changes, etc.)
  const isStepReviewMode =
    guidedPhase === 'execution' &&
    !autoApprove &&
    !isSending &&
    buildPlan?.approved &&
    !isSimpleBuild;

  // Hide the full prompt input and show the intervention bar instead
  const hidePromptInput =
    hasPendingGuidedResult ||
    (isGuidedMode &&
      (showGuidedPanel ||
        (guidedPhase === 'ideation' && !hasMessages) ||
        isGuidedLoading ||
        (guidedPhase === 'execution' && !showRefineState && !isStepReviewMode)));

  // Show "Resume Guided Flow" when the user has a guided flow but the guided panel isn't visible.
  // This covers: chat mode, conversation view when a panel exists, or ideation with messages.
  const showResumeGuidedButton =
    guidedFlow != null &&
    !isGuidedLoading &&
    !isSending &&
    !hasPendingGuidedResult &&
    !showGuidedPanel &&
    // Don't show during execution (has its own controls) or when the welcome screen is up
    guidedPhase !== 'execution' &&
    guidedPhase !== 'complete' &&
    !(guidedPhase === 'ideation' && !hasMessages);

  const panelToggleLabel = hasClarificationPanel
    ? 'Questions'
    : hasPlanningPanel || isPlanGenerating
      ? 'Plan'
      : hasAssumptionsPanel
        ? 'Review'
        : isGuidedLoadingWithCancel
          ? 'Loading'
          : '';

  // ─── Guided content for ConversationPane slots ──────────────────────────────

  const guidedEmptyContent =
    isGuidedMode && guidedPhase === 'ideation' && !hasMessages ? (
      <PluginGuidedWelcome
        onStart={handleGuidedStart}
        isProcessing={isGuidedLoading}
        idea={guidedIdea}
        onIdeaChange={setGuidedIdea}
        name={guidedName}
        onNameChange={setGuidedName}
        selectedThemeId={selectedThemeId}
        onThemeChange={(id, ct) => {
          setSelectedThemeId(id);
          setCustomTheme(ct);
        }}
        selectedFontPairingId={selectedFontPairingId}
        onFontPairingChange={setSelectedFontPairingId}
      />
    ) : undefined;

  const guidedHeaderContent = isGuidedMode ? (
    showRefineState ? (
      <CompletionBanner buildPlan={buildPlan ?? null} />
    ) : null
  ) : null;

  const guidedFooterContent =
    isGuidedMode &&
    guidedPhase === 'execution' &&
    buildPlan?.approved &&
    !showRefineState &&
    !isSimpleBuild ? (
      <StepExecutionPanel
        buildPlan={buildPlan}
        autoApprove={autoApprove}
        onAutoApproveChange={setAutoApprove}
        onStopGeneration={stopGeneration}
        onSendFeedback={handleExecutionFeedback}
        onAdvanceStep={advanceStep}
        isProcessing={isSending}
      />
    ) : null;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <PluginBuilderHeader
        session={session}
        sessionId={sessionId!}
        files={files}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
        onConfigure={() => setConfigDialogOpen(true)}
        onReportProblem={() => setReportDialogOpen(true)}
        onSaveNameDescription={async (name, description, expandedDescription) => {
          setSessionName(name);
          if (sessionId) {
            await api.updatePluginBuilderSession(sessionId, {
              currentName: name,
              currentDescription: description,
              currentExpandedDescription: expandedDescription,
            });
          }
        }}
        isPublishing={isPublishing}
        publishStatus={publishStatus}
        hasUnpublishedChanges={hasUnpublishedChanges}
      />

      {/* SDK update banner */}
      {sdkUpdate?.available && (
        <SdkUpdateBanner
          from={sdkUpdate.from}
          to={sdkUpdate.to}
          hasBreaking={sdkUpdate.hasBreaking}
          isUpgrading={isUpgradingSdk}
          onUpgrade={upgradeSdk}
        />
      )}

      {/* Main content wrapper */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Guided phase bar */}
        {isGuidedMode && guidedFlow && (
          <GuidedPhaseBar
            currentPhase={showRefineState ? 'complete' : guidedPhase}
            buildPlan={buildPlan}
            onPhaseClick={handlePhaseClick}
          />
        )}

        {/* Scroll container */}
        <div
          ref={panelsContainerRef}
          className="flex-1 min-h-0 overflow-x-auto md:overflow-x-hidden overflow-y-hidden scrollbar-hide"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* Inner row */}
          <div className="h-full flex flex-row w-[200vw] md:w-full">
            {/* Left: Conversation or guided panel */}
            <div
              ref={conversationPaneRef}
              onMouseDown={() => handlePaneFocus(conversationPaneRef)}
              className="w-[100vw] md:w-1/2 border-r flex flex-col min-h-0"
            >
              {/* Panel/Chat view toggle — shown when a guided panel is available */}
              {hasGuidedPanel && (
                <div className="flex items-center border-b bg-muted/20 px-3 py-1.5 gap-1 flex-shrink-0">
                  <button
                    onClick={() => setGuidedViewMode('panel')}
                    className={`text-xs font-medium px-3 py-1 rounded-md transition-colors ${guidedViewMode === 'panel' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {panelToggleLabel}
                  </button>
                  <button
                    onClick={() => setGuidedViewMode('conversation')}
                    className={`text-xs font-medium px-3 py-1 rounded-md transition-colors ${guidedViewMode === 'conversation' ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    Chat
                  </button>
                </div>
              )}

              {/* Full-panel guided content */}
              {showGuidedPanel && hasClarificationPanel ? (
                <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">
                  {verbose &&
                    (() => {
                      const lastAssistant = [...messages]
                        .reverse()
                        .find((m) => m.role === 'assistant');
                      const events = lastAssistant
                        ? verboseHistory[lastAssistant.timestamp || ''] || []
                        : [];
                      return events.length > 0 ? (
                        <div className="px-4 pt-4">
                          <VerboseTimeline events={events} />
                        </div>
                      ) : null;
                    })()}
                  <ClarificationControls
                    clarifyingQA={clarifyingQA}
                    onSubmitAnswers={handleSubmitAnswers}
                    isProcessing={isGuidedLoading}
                    onStop={cancelGuidedRequest}
                  />
                </div>
              ) : showGuidedPanel && (isPlanGenerating || isGuidedLoadingWithCancel) ? (
                <div className="flex-1 flex flex-col p-6 space-y-4 overflow-y-auto">
                  <div className="flex items-center gap-3">
                    <div className="relative flex-shrink-0">
                      <ClipboardList className="h-8 w-8 text-muted-foreground/40" />
                      <Loader2 className="h-4 w-4 animate-spin text-primary absolute -bottom-1 -right-1" />
                    </div>
                    <div>
                      <h3 className="text-base font-semibold">
                        {isGuidedLoadingWithCancel
                          ? guidedLoadingMessage.title
                          : 'Generating your build plan...'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {isGuidedLoadingWithCancel
                          ? guidedLoadingMessage.subtitle
                          : 'Researching patterns and designing the steps for your plugin.'}
                      </p>
                    </div>
                  </div>
                  {verbose && pendingVerboseEvents.length > 0 && (
                    <VerboseTimeline events={pendingVerboseEvents} isPending />
                  )}
                  {isGuidedLoading && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={cancelGuidedRequest}
                      className="w-fit"
                    >
                      <Square className="h-3.5 w-3.5 mr-2 fill-current" />
                      Stop
                    </Button>
                  )}
                </div>
              ) : showGuidedPanel && hasPlanningPanel ? (
                <PlanReview
                  buildPlan={buildPlan!}
                  onApprove={handlePlanReviewApprove}
                  onRequestChanges={handlePlanReviewChanges}
                  onToggleNodeUI={handleToggleNodeUI}
                  isProcessing={isGuidedLoading}
                />
              ) : showGuidedPanel && hasAssumptionsPanel ? (
                <AssumptionReview
                  assumptions={guidedFlow?.assumptions ?? []}
                  buildPlan={buildPlan ?? null}
                  onSuggestChanges={handleSuggestAssumptionChanges}
                  onApprovePlan={handleApprovePlan}
                  isProcessing={isGuidedLoading}
                />
              ) : (
                <PluginConversationPane
                  messages={messages}
                  isProcessing={isSending || isGuidedLoading}
                  streamingText={streamingText}
                  suggestedNextSteps={suggestedNextSteps}
                  verboseHistory={verbose ? verboseHistory : undefined}
                  pendingVerboseEvents={verbose ? pendingVerboseEvents : undefined}
                  onSuggestionClick={setInputValue}
                  emptyStateContent={guidedEmptyContent}
                  headerContent={guidedHeaderContent}
                  footerContent={guidedFooterContent}
                  isAdmin={isAdmin}
                  className="flex-1 min-h-0"
                />
              )}

              {/* Desktop-only: prompt input or intervention bar */}
              {hidePromptInput ? (
                <div className="hidden md:block">
                  {hasPendingGuidedResult ? (
                    <div className="border-t px-4 py-2">
                      <Button onClick={handleContinueTransition} size="sm" className="gap-2">
                        Continue
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : isGuidedLoading ? (
                    <div className="border-t px-4 py-2">
                      <Button
                        onClick={cancelGuidedRequest}
                        variant="destructive"
                        size="sm"
                        className="gap-2"
                      >
                        <Square className="h-3.5 w-3.5 fill-current" />
                        Stop
                      </Button>
                    </div>
                  ) : guidedFooterContent ? null : (
                    <GuidedInterventionBar
                      isProcessing={isSending}
                      onStop={handleStopFromPanel}
                      onSendFeedback={handleExecutionFeedback}
                    />
                  )}
                </div>
              ) : (
                <div className="hidden md:block">
                  {showResumeGuidedButton && (
                    <div className="border-t px-4 py-2 bg-muted/30">
                      <Button
                        onClick={handleResumeGuidedFlow}
                        variant="outline"
                        size="sm"
                        className="gap-2 w-full"
                      >
                        Resume Guided Flow
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                  <PluginPromptInput {...promptInputProps} />
                </div>
              )}
            </div>

            {/* Right: Preview */}
            <div
              ref={previewPaneRef}
              onMouseDown={() => handlePaneFocus(previewPaneRef)}
              className="w-[100vw] md:w-1/2 flex flex-col min-h-0"
            >
              <PluginPreviewPane
                previewHtml={previewHtml}
                files={files}
                isBuildingPreview={isBuildingPreview}
                buildError={buildError}
                onBuildPreview={() => buildPreview(files ?? undefined)}
                onUndo={undo}
                onRedo={redo}
                canUndo={canUndo}
                canRedo={canRedo}
                undoCount={undoCount}
                redoCount={redoCount}
                verbose={verbose}
                onVerboseChange={setVerbose}
                deepThinking={deepThinking}
                onDeepThinkingChange={setDeepThinking}
                isAdmin={isAdmin}
                processGraph={buildPlan?.graph}
                currentNodeId={buildPlan?.currentNodeId}
                aiConfigs={aiConfigs}
                modelDefinitions={modelDefinitions}
                logEntries={logEntries}
                onClearLogs={() => setLogEntries([])}
                onScreenshotCaptured={uploadPreviewScreenshot}
                previewTheme={previewTheme}
                onPreviewThemeToggle={() =>
                  setPreviewTheme((prev) => (prev === 'dark' ? 'light' : 'dark'))
                }
                iframeRef={previewIframeRef}
                bridgeRef={bridgeRef}
                onPreviewIframeLoad={handlePreviewIframeLoad}
                className="flex-1 min-h-0"
              />
            </div>
          </div>
        </div>

        {/* Mobile-only slider with nav buttons */}
        <div className="md:hidden flex-shrink-0 border-t border-border bg-muted/30 px-10 py-2 flex items-center gap-1">
          <button
            onClick={() => scrollToPanel('left')}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-5 w-5 text-muted-foreground" />
          </button>

          <div className="flex-1 max-w-[70%] mx-auto">
            <div
              ref={scrollTrackRef}
              className="relative h-6 cursor-pointer flex items-center"
              onMouseDown={(e) => {
                e.preventDefault();
                isDraggingRef.current = true;
                handleScrollbarDrag(e.clientX);

                const handleMouseMove = (moveEvent: MouseEvent) => {
                  handleScrollbarDrag(moveEvent.clientX);
                };
                const handleMouseUp = () => {
                  isDraggingRef.current = false;
                  document.removeEventListener('mousemove', handleMouseMove);
                  document.removeEventListener('mouseup', handleMouseUp);
                };
                document.addEventListener('mousemove', handleMouseMove);
                document.addEventListener('mouseup', handleMouseUp);
              }}
              onTouchStart={(e) => {
                e.preventDefault();
                isDraggingRef.current = true;
                const touch = e.touches[0];
                handleScrollbarDrag(touch.clientX);

                const handleTouchMove = (moveEvent: TouchEvent) => {
                  const moveTouch = moveEvent.touches[0];
                  handleScrollbarDrag(moveTouch.clientX);
                };
                const handleTouchEnd = () => {
                  isDraggingRef.current = false;
                  document.removeEventListener('touchmove', handleTouchMove);
                  document.removeEventListener('touchend', handleTouchEnd);
                };
                document.addEventListener('touchmove', handleTouchMove, { passive: false });
                document.addEventListener('touchend', handleTouchEnd);
              }}
            >
              <div className="absolute inset-x-0 h-1 bg-muted rounded-full" />
              <div
                className="absolute w-6 h-6 bg-primary/80 hover:bg-primary rounded-full transition-colors shadow-sm"
                style={{
                  left: (() => {
                    const maxScroll = scrollState.scrollWidth - scrollState.clientWidth;
                    if (maxScroll <= 0) return '0px';
                    const ratio = scrollState.scrollLeft / maxScroll;
                    return `calc(${ratio * 100}% - ${ratio * 24}px)`;
                  })(),
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              />
            </div>
          </div>

          <button
            onClick={() => scrollToPanel('right')}
            className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>

        {/* Mobile-only: prompt input or intervention bar */}
        {hidePromptInput ? (
          <div className="md:hidden">
            {hasPendingGuidedResult ? (
              <div className="border-t px-4 py-2">
                <Button onClick={handleContinueTransition} size="sm" className="gap-2">
                  Continue
                  <ChevronRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : isGuidedLoading ? (
              <div className="border-t px-4 py-2">
                <Button
                  onClick={cancelGuidedRequest}
                  variant="destructive"
                  size="sm"
                  className="gap-2"
                >
                  <Square className="h-3.5 w-3.5 fill-current" />
                  Stop
                </Button>
              </div>
            ) : guidedFooterContent ? null : (
              <GuidedInterventionBar
                isProcessing={isSending}
                onStop={handleStopFromPanel}
                onSendFeedback={handleExecutionFeedback}
              />
            )}
          </div>
        ) : (
          <div className="md:hidden">
            {showResumeGuidedButton && (
              <div className="border-t px-4 py-2 bg-muted/30">
                <Button
                  onClick={handleResumeGuidedFlow}
                  variant="outline"
                  size="sm"
                  className="gap-2 w-full"
                >
                  Resume Guided Flow
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            <PluginPromptInput {...promptInputProps} />
          </div>
        )}
      </div>

      {/* Configure Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Plugin Settings</DialogTitle>
            <DialogDescription>
              Configure your arche&apos;s name, description, pricing, visibility, and theme.
            </DialogDescription>
          </DialogHeader>
          <PluginConfigPanel
            session={session}
            onSettingsChange={handleSettingsChange}
            onNameChange={handleConfigNameChange}
            onDescriptionChange={handleConfigDescriptionChange}
            onExpandedDescriptionChange={handleConfigExpandedDescriptionChange}
            isPublished={session?.isPublished}
            className="py-4"
          />
        </DialogContent>
      </Dialog>

      {/* Report Problem Dialog */}
      <PluginReportProblemDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        session={session}
        files={files}
        logEntries={logEntries}
        guidedFlow={guidedFlow}
        buildPlan={buildPlan}
        guidedBuilderState={{ mode: builderMode, phase: guidedPhase, autoApprove }}
      />

      {/* AI Config Editor Dialog */}
      <AiConfigEditor
        config={aiConfigs.find((c) => c.configId === editingConfigId) ?? null}
        open={editingConfigId !== null}
        onClose={() => setEditingConfigId(null)}
        onSave={(updated) => {
          const updatedConfigs = aiConfigs.map((c) =>
            c.configId === updated.configId ? updated : c,
          );
          const settings = { ...(currentSettings ?? {}), aiConfigs: updatedConfigs };
          if (sessionId) {
            api.updatePluginBuilderSession(sessionId, { currentSettings: settings });
          }
        }}
      />
    </div>
  );
}
